import { type DynamicDateFilter, type DynamicFilterValue, getDateQuery, parseDynamicFilters } from '../../filters';

/**
 * Ticket list / search status values accepted by {@link buildTicketSearchFilters}.
 * Aligns with overview UI ticket status string values (includes archived).
 */
export enum TicketStatusFilter {
	DRAFT = 'draft',
	OPEN = 'open',
	IN_PROGRESS = 'in_progress',
	CLOSED = 'closed',
	CANCELLED = 'cancelled',
	ARCHIVED = 'archived',
}

export type TicketListName = [string, 'or' | 'and'];

export type TicketSearchFiltersQuery = {
	listNames?: TicketListName[];
	location__in?: string[];
	label__in?: (string | null)[];
	ticketTypeId__in?: string[];
	priority__in?: string[];
	reporter__in?: string[];
	assignee__in?: (string | null)[];
	status__in?: string[];
	cancelled__ne?: null;
	archived__ne?: null;
	$or?: Record<string, unknown>[];
	reservations__in?: string[];
	orders__in?: string[];
	items__in?: string[];
	kits__in?: string[];
	tickets__in?: string[];
	[key: string]: unknown;
};

export type BuildTicketSearchFiltersInput = {
	statuses?: string[];
	locationIds?: string[];
	labelIds?: string[];
	reporterIds?: string[];
	assigneeIds?: string[];
	ticketTypeIds?: string[];
	priorities?: string[];
	reservationIds?: string[];
	orderIds?: string[];
	itemIds?: string[];
	kitIds?: string[];
	ticketIds?: string[];
	created?: DynamicDateFilter;
	started?: DynamicDateFilter;
	closed?: DynamicDateFilter;
	cancelled?: DynamicDateFilter;
	archived?: DynamicDateFilter;
	modified?: DynamicDateFilter;
	dynamicFilters?: Record<string, DynamicFilterValue | undefined>;
};

/**
 * Builds Python ticket search / `customFieldFilters` params from neutral filter ids.
 * UI adapters should unwrap chip shapes before calling this.
 */
export const buildTicketSearchFilters = (input: BuildTicketSearchFiltersInput = {}): TicketSearchFiltersQuery => {
	const {
		statuses,
		locationIds,
		labelIds,
		reporterIds,
		assigneeIds,
		ticketTypeIds,
		priorities,
		reservationIds,
		orderIds,
		itemIds,
		kitIds,
		ticketIds,
		created,
		started,
		closed,
		cancelled,
		archived,
		modified,
		dynamicFilters = {},
	} = input;

	let query: TicketSearchFiltersQuery = {
		listNames: [],
	};

	const hasStatuses = Boolean(statuses && statuses.length > 0);

	if (!hasStatuses) {
		query.listNames = [['unarchived', 'or']];
	}

	if (hasStatuses && statuses) {
		const statusToFilter: string[] = [];
		const hasCancelled = statuses.includes(TicketStatusFilter.CANCELLED);
		const hasArchived = statuses.includes(TicketStatusFilter.ARCHIVED);

		for (const status of statuses) {
			if (status !== TicketStatusFilter.CANCELLED && status !== TicketStatusFilter.ARCHIVED) {
				statusToFilter.push(status);
			}
		}

		const hasOtherStatuses = statusToFilter.length > 0;
		const needsOrCondition = (hasCancelled || hasArchived) && hasOtherStatuses;

		if (needsOrCondition) {
			const orConditions: Record<string, unknown>[] = [];
			if (hasCancelled) {
				orConditions.push({ cancelled__ne: null });
			}
			if (hasArchived) {
				orConditions.push({ archived__ne: null });
			}
			if (hasOtherStatuses) {
				orConditions.push({ status__in: statusToFilter });
			}
			query.$or = orConditions;
		} else {
			if (hasCancelled && hasArchived) {
				query.$or = [{ cancelled__ne: null }, { archived__ne: null }];
				statusToFilter.push(TicketStatusFilter.CLOSED);
			} else if (hasCancelled) {
				query.cancelled__ne = null;
				statusToFilter.push(TicketStatusFilter.CLOSED);
			} else if (hasArchived) {
				query.archived__ne = null;
				statusToFilter.push(TicketStatusFilter.CLOSED);
			}

			if (statusToFilter.length > 0) {
				query.status__in = statusToFilter;
			}
		}
	}

	if (locationIds && locationIds.length > 0) {
		query.location__in = locationIds;
	}

	if (labelIds && labelIds.length > 0) {
		query.label__in = labelIds.map((id) => (id === '' ? null : id));
	}

	if (reporterIds && reporterIds.length > 0) {
		query.reporter__in = reporterIds;
	}

	if (assigneeIds && assigneeIds.length > 0) {
		query.assignee__in = assigneeIds.map((id) => (id === '' ? null : id));
	}

	if (ticketTypeIds && ticketTypeIds.length > 0) {
		if (ticketTypeIds.length === 1) {
			query.ticketTypeId = ticketTypeIds[0];
		} else {
			query.ticketTypeId__in = ticketTypeIds;
		}
	}

	if (priorities && priorities.length > 0) {
		query.priority__in = priorities;
	}

	if (reservationIds && reservationIds.length > 0) {
		query.reservations__in = reservationIds;
	}

	if (orderIds && orderIds.length > 0) {
		query.orders__in = orderIds;
	}

	if (itemIds && itemIds.length > 0) {
		query.items__in = itemIds;
	}

	if (kitIds && kitIds.length > 0) {
		query.kits__in = kitIds;
	}

	if (ticketIds && ticketIds.length > 0) {
		query.tickets__in = ticketIds;
	}

	if (created) {
		query = { ...query, ...getDateQuery('created', created) };
	}

	if (started) {
		query = { ...query, ...getDateQuery('started', started) };
	}

	if (closed) {
		query = { ...query, ...getDateQuery('closed', closed) };
	}

	if (cancelled) {
		query = { ...query, ...getDateQuery('cancelled', cancelled) };
	}

	if (archived) {
		query = { ...query, ...getDateQuery('archived', archived) };
	}

	if (modified) {
		query = { ...query, ...getDateQuery('modified', modified) };
	}

	return {
		...query,
		...parseDynamicFilters(dynamicFilters),
	};
};
